import React from 'react';
import { useAccount, useConnect, useDisconnect } from 'wagmi';
import { injected } from 'wagmi/connectors';
import { useAuth } from '../contexts/AuthContext';

/**
 * 钱包连接组件 - 集成认证功能
 */
export default function WalletConnect({ variant = 'default' }) {
  const { address, isConnected } = useAccount();
  const { connect } = useConnect();
  const { disconnect } = useDisconnect();
  const { 
    isAuthenticated, 
    isAuthenticating, 
    needsSignIn, 
    signInWithWallet, 
    signOut,
    authError,
    clearError
  } = useAuth();

  // 调试信息（生产环境中可移除）
  // console.log('WalletConnect 状态:', {
  //   isConnected,
  //   address,
  //   isAuthenticated,
  //   needsSignIn,
  //   isAuthenticating
  // });

  const handleConnect = () => {
    connect({ connector: injected() });
  };

  const handleDisconnect = async () => {
    // console.log('🔌 开始断开连接流程...');
    
    try {
      // 1. 先清除错误状态
      clearError();
      // console.log('🧹 错误状态已清除');
      
      // 2. 登出后端认证（signOut已经有完善的错误处理）
      // console.log('🔐 开始后端登出...');
      await signOut();
      // console.log('✅ 后端登出完成');
      
    } catch (error) {
      // signOut已经有完善的错误处理，这里不应该抛出错误
      // 但为了防万一，仍然捕获并记录
      // console.error('⚠️ 登出流程中出现意外错误:', error);
    } finally {
      // 3. 无论登出是否成功，都要断开钱包连接
      try {
        // console.log('🔌 断开钱包连接...');
        disconnect();
        // console.log('✅ 钱包连接已断开');
      } catch (disconnectError) {
        // console.error('⚠️ 断开钱包连接失败:', disconnectError);
        // 即使断开失败也不抛出错误，让用户界面正常更新
      }
      
      // console.log('✅ 断开连接流程完成');
    }
  };

  const handleSignIn = async () => {
    try {
      // 清除之前的错误
      clearError();
      await signInWithWallet();
    } catch (error) {
      // console.error('登录失败:', error);
      // 如果是服务器错误或者token过期，建议重新连接
      if (error.message?.includes('500') || error.message?.includes('Failed to fetch')) {
        // console.log('⚠️ 检测到服务器错误，建议重新连接钱包');
        // 可以在这里显示一个重置按钮或者自动重置
      }
    }
  };

  // 重置连接（清除所有状态并重新开始）
  const handleReset = async () => {
    try {
      // console.log('🔄 重置钱包连接...');
      
      // 1. 清除后端认证状态
      await signOut();
      
      // 2. 断开钱包连接
      disconnect();
      
      // 3. 清除错误状态
      clearError();
      
      // 4. 清除本地存储
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      
      // console.log('✅ 重置完成，请重新连接钱包');
    } catch (error) {
      // console.error('重置失败:', error);
    }
  };

  // 未连接钱包
  if (!isConnected) {
    return (
      <button
        onClick={handleConnect}
        type="button"
        className={variant === 'menu' 
          ? "block w-full text-left px-4 py-2 text-sm text-white hover:bg-white/10 transition-colors"
          : "px-4 py-1.5 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 rounded-lg text-white font-medium transition-all duration-200 transform hover:scale-105 text-sm"
        }
      >
        Connect Wallet
      </button>
    );
  }

  // 已连接但未认证（显示登录状态或错误）
  // 如果有任何错误，都应该回到Connect Wallet状态
  if (needsSignIn && !authError) {
    return (
      <div className="flex flex-col items-end space-y-1">
        <div className="flex items-center space-x-2">
          <span className="text-xs text-gray-400">
            {address?.slice(0, 6)}...{address?.slice(-4)}
          </span>
          {isAuthenticating ? (
            // 正在签名状态
            <div className="px-3 py-1.5 bg-yellow-500/20 rounded-lg">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                <span className="text-sm text-yellow-400">Signing...</span>
              </div>
            </div>
          ) : authError ? (
            // 有错误时显示错误状态，不显示重试按钮
            <div className="px-3 py-1.5 bg-red-500/20 rounded-lg">
              <span className="text-sm text-red-400">Error</span>
            </div>
          ) : (
            // 等待自动签名
            <div className="px-3 py-1.5 bg-blue-500/20 rounded-lg">
              <span className="text-sm text-blue-400">Connecting...</span>
            </div>
          )}
        </div>
        {authError && (
          <div className="text-xs text-red-400 max-w-xs text-right space-y-1">
            <div>{authError}</div>
            {(authError.includes('Failed to fetch') || authError.includes('500') || authError.includes('签名验证失败')) && (
              <button
                onClick={handleReset}
                className="text-xs text-blue-400 hover:text-blue-300 underline"
              >
                重置连接
              </button>
            )}
            {authError.includes('您的账户已在其他设备登录') && (
              <button
                onClick={() => {
                  clearError();
                  handleConnect();
                }}
                className="text-xs text-blue-400 hover:text-blue-300 underline"
              >
                重新连接钱包
              </button>
            )}

          </div>
        )}
      </div>
    );
  }

  // 已认证
  return (
    <button
      onClick={handleDisconnect}
      type="button"
      className={variant === 'menu'
        ? "block w-full text-left px-4 py-2 text-sm text-green-400 hover:bg-white/10 transition-colors"
        : "px-4 py-1.5 bg-gradient-to-r from-red-500/30 to-red-600/30 hover:from-red-600/30 hover:to-red-700/30 rounded-lg text-white font-medium transition-all duration-200 text-sm"
      }
    >
      Disconnect
    </button>
  );
}
